import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import InputComponent from '../input';
import { CheckSvg, ChevronRightSvg } from '../../assets/svgs';
import { MultiDropdownOptions, MultiSelectDropdownWrapper } from './multi-select-dropdown.style';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const defaultOptions = [
    { value: 1, label: 'option 1' },
    { value: 2, label: 'option 2' },
    { value: 3, label: 'option 3' },
];

const MultiSelectDropdownComponent = ({ options = defaultOptions, label, name, value, onChange, disabled, mui }) => {
    const dropdownRef = useRef(null);
    const [selected, setSelected] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const handleDropdown = () => {
        if (!disabled) {
            setDropdownOpen(!dropdownOpen);
        }
    };

    const handleOptionClick = (event, option) => {
        event.stopPropagation(); // Prevent event bubbling to parent dropdown handler
        if (!disabled) {
            setSelected((old) => {
                // Check if the option is already selected
                const isAlreadySelected = Array.isArray(old) && old.some((item) => item.value === option.value);

                // If already selected, remove it; otherwise, add it
                const newSelected = isAlreadySelected
                    ? old.filter((item) => item.value !== option.value) // Remove the option
                    : [...old, option]; // Add the option

                // Trigger the onChange callback if provided
                if (onChange) {
                    const selectedValues = newSelected.map((item) => item.value); // Extract values from the selected array
                    onChange({
                        target: {
                            name,
                            value: selectedValues, // Pass the array of values
                        },
                    });
                }

                return newSelected;
            });
        }
    };

    useEffect(() => {
        if (!Array.isArray(value) || !Array.isArray(options)) return; // Early exit if `value` or `options` is invalid

        // Extract unique values from the current `value` array
        const newValues = [...new Set(value)]; // Remove duplicates from `value`

        // Find all matching options in `options` for the given `value` array
        const optionsToAdd = options.filter((option) =>
            newValues.includes(option?.value)
        );

        // Only update if the selected values are actually different from what we need
        const currentSelectedValues = selected.map(item => item.value).sort();
        const newSelectedValues = optionsToAdd.map(item => item.value).sort();
        
        // Check if arrays are different
        const isDifferent = currentSelectedValues.length !== newSelectedValues.length ||
            currentSelectedValues.some((val, idx) => val !== newSelectedValues[idx]);

        if (isDifferent) {
            setSelected(optionsToAdd);
        }
    }, [value, options]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <>
            {
                mui ? (
                    <Autocomplete
                        disablePortal
                        multiple
                        options={options}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={(option, value) => option.value === (value?.value ?? value)}
                        value={selected}
                        disabled={disabled}
                        onChange={(event, newValue) => {
                            setSelected(newValue);
                            if (onChange) {
                                const selectedValues = newValue.map((item) => item.value);
                                onChange({
                                    target: { name, value: selectedValues },
                                });
                            }
                        }}
                        disableCloseOnSelect
                        renderInput={(params) => (
                            <TextField {...params} name={name} label={label} variant="filled" />
                        )}
                    />
                ) : (
                    <MultiSelectDropdownWrapper onClick={handleDropdown} ref={dropdownRef}>
                        <InputComponent
                            placeholder="Select"
                            type="text"
                            name={name}
                            value={
                                (selected && Array.isArray(selected) && selected.length > 0) ? (
                                    selected.map((e) => e?.label).join(', ')
                                ) : ''
                            }
                            disabled={disabled}
                            readOnly
                        />
                        <ChevronRightSvg className={`${dropdownOpen ? 'chevron-rotate' : ''} chevron-icon`} />
                        {
                            dropdownOpen && (
                                <MultiDropdownOptions className="custom-scrollbar box-shadow">
                                    <ul>
                                        {
                                            options && options?.length > 0 && options?.map((option, index) => {
                                                const isSelected = selected && Array.isArray(selected) && selected.length > 0 && selected.some((item) => item.value === option?.value);

                                                return (
                                                    <li
                                                        key={index}
                                                        onClick={(event) => handleOptionClick(event, option)}
                                                        className={isSelected ? 'active' : ''}
                                                    >
                                                        {option.label}
                                                        {
                                                            isSelected &&
                                                            <CheckSvg />
                                                        }
                                                    </li>
                                                )
                                            })
                                        }
                                        {
                                            (options === undefined || options.length < 1) &&
                                            <li>No data found!</li>
                                        }
                                    </ul>
                                </MultiDropdownOptions>
                            )
                        }
                    </MultiSelectDropdownWrapper>
                )
            }
        </>
    );
};

MultiSelectDropdownComponent.propTypes = {
    options: PropTypes.array,
    name: PropTypes.string,
    value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    mui: PropTypes.bool,
};

export default MultiSelectDropdownComponent;
